/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

export type NavContentSlotEntry = {
    content: PageWithComponentData | null;
    ribbon: PageWithComponentData | null;
};

/**
 * A lightweight external store for the mega-menu's Page Designer content slots
 * (`menu-{categoryId}` / `menu-{categoryId}-ribbon`), keyed by top-level category id.
 * Modeled after {@link SubCategoryStore} in `@/components/navigation-menu/context`, minus
 * the React Context: `ResponsiveNavigationMenu` owns the only instance and both reads it
 * (via closures in `propsContent`/`renderSlotListAfter`) and subscribes to it (via
 * `useSyncExternalStore`) directly, so no context indirection is needed here.
 */
export type NavContentSlotStore = ReturnType<typeof createNavContentSlotStore>;

export function createNavContentSlotStore() {
    let data = new Map<string, NavContentSlotEntry>();
    const listeners = new Set<() => void>();

    return {
        update(this: void, entries: Map<string, NavContentSlotEntry>): void {
            data = entries;
            listeners.forEach((l) => l());
        },
        subscribe(this: void, listener: () => void): () => void {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getSnapshot(this: void): Map<string, NavContentSlotEntry> {
            return data;
        },
    };
}
